// biome-ignore-all lint/suspicious/noExplicitAny: not changing all these now
import {
  type InfiniteData,
  type QueryKey,
  type UseInfiniteQueryResult,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
// import { getIdeaFieldsWithMissingCustomFieldsAdded } from '../utils/Utilities';
import type {
  ActivityLogDto,
  CreateIdeaAttachmentDto,
  CreateIdeaCommentDto,
  CreateIdeaDto,
  CreateIdeaFromFeedbackDto,
  FeedbackDto,
  FeedbackSummaryDto,
  FilterSchemaDto,
  GetIdeaAttachmentResponseDto,
  GetIdeaAttachmentsResponseDto,
  GetIdeaCommentResponseDto,
  GetIdeaRecipientsResponseDto,
  GetIdeaResponseDto,
  GetIdeasRequiringReviewResponseDto,
  GetIdeaViewByIdResponseDto,
  GetLinkedRoadmapItemsResponseDto,
  IdeaCommentsDto,
  IdeaDto,
  IdeaPortalAssociationDto,
  IdeaStatusSummaryDto,
  IdeaSummaryDto,
  LifecycleFilterType,
  LinkedRoadmapFilterDto,
  LinkedRoadmapItemDto,
  MatrixIdeaSummaryDto,
  ReviewReasonType,
  UpdateIdeaCommentDto,
  UpdateIdeaDto,
  UpdateIdeaSummaryResponseDto,
} from '@tempo-io/fim3-dto';
import { type GetAllParamsDto, LifecycleFilter } from '@tempo-io/fim3-dto';
import type { AxiosInstance } from 'axios';
import { getIdeaFieldsWithMissingCustomFieldsAdded } from '../utils/Utilities';
import { useErrorHandlingMutation } from './useErrorHandlingMutation';
import { getUserDataForResults } from './useFim3Api';
import { useS3Api } from './useS3Api';

export const useIdeasApi = (httpClient: AxiosInstance) => {
  const baseUrl = `/ideas`;
  const s3Api = useS3Api(httpClient);
  const defaultLimit = 25;

  const createIdea = async (idea: CreateIdeaDto): Promise<GetIdeaResponseDto> => {
    const { data } = await httpClient.post(`${baseUrl}`, idea);
    return data;
  };
  const createIdeaFromFeedback = async (
    idf: CreateIdeaFromFeedbackDto,
  ): Promise<GetIdeaResponseDto> => {
    const { data } = await httpClient.post(`${baseUrl}/from-feedback`, idf);
    return data;
  };

  const updateIdea = async (
    updateIdeaArg: UpdateIdeaDto & { id: number },
  ): Promise<GetIdeaResponseDto> => {
    const { data } = await httpClient.put(`${baseUrl}/${updateIdeaArg.id}`, {
      ...updateIdeaArg,
      id: undefined,
    });
    return data;
  };

  const bulkArchiveIdeas = async (ids: number[]): Promise<IdeaDto[]> => {
    const { data } = await httpClient.post(`${baseUrl}/bulk-archive`, { ids });
    return data;
  };

  const bulkRestoreIdeas = async (ids: number[]): Promise<IdeaDto[]> => {
    const { data } = await httpClient.post(`${baseUrl}/bulk-restore`, { ids });
    return data;
  };

  const bulkDeleteIdeas = async (ids: number[]): Promise<IdeaDto[]> => {
    const { data } = await httpClient.post(`${baseUrl}/bulk-delete`, { ids });
    return data;
  };

  const archiveIdea = async (id: number): Promise<IdeaDto> => {
    const { data } = await httpClient.put(`${baseUrl}/${id}/archive`);
    return data;
  };

  const restoreIdea = async (id: number): Promise<IdeaDto> => {
    const { data } = await httpClient.put(`${baseUrl}/${id}/restore`);
    return data;
  };

  const deleteIdea = async (id: number): Promise<IdeaDto> => {
    const { data } = await httpClient.delete(`${baseUrl}/${id}`);
    return data;
  };

  const createIdeaComment = async (
    comment: CreateIdeaCommentDto,
  ): Promise<GetIdeaCommentResponseDto> => {
    const { data } = await httpClient.post(`${baseUrl}/${comment.ideaId}/comments`, comment);
    return data;
  };
  const updateIdeaComment = async (
    comment: UpdateIdeaCommentDto & { id: number; ideaId: number },
  ): Promise<GetIdeaCommentResponseDto> => {
    const { id, ideaId, ...rest } = comment;
    const { data } = await httpClient.put(`${baseUrl}/${ideaId}/comments/${id}`, {
      comment: rest.comment,
      internal: rest.internal,
    });
    return data;
  };

  const archiveComment = async (params: {
    id: number;
    ideaId: number;
  }): Promise<GetIdeaCommentResponseDto> => {
    const { data } = await httpClient.put(
      `${baseUrl}/${params.ideaId}/comment/${params.id}/archive`,
    );
    return data;
  };

  const restoreComment = async (params: {
    id: number;
    ideaId: number;
  }): Promise<GetIdeaCommentResponseDto> => {
    const { data } = await httpClient.put(
      `${baseUrl}/${params.ideaId}/comment/${params.id}/restore`,
    );
    return data;
  };

  const deleteComment = async (params: {
    id: number;
    ideaId: number;
  }): Promise<GetIdeaCommentResponseDto> => {
    const { data } = await httpClient.delete(`${baseUrl}/${params.ideaId}/comment/${params.id}`);
    return data;
  };

  const getIdeaFeedback = async (id: number): Promise<FeedbackDto[] | FeedbackSummaryDto> => {
    const { data } = await httpClient.get(`${baseUrl}/${id}/feedback`);
    return data;
  };

  const getIdeaCount = async (
    lifecycleFilter?: LifecycleFilterType | undefined,
    search?: string | undefined,
    filter?: FilterSchemaDto,
  ): Promise<number> => {
    const { data } = await httpClient.post(`${baseUrl}/count`, { lifecycleFilter, search, filter });
    return data;
  };

  const createIdeaAttachment = async ({
    file,
    attachment,
  }: {
    file: File;
    attachment: CreateIdeaAttachmentDto;
  }): Promise<GetIdeaAttachmentResponseDto> => {
    const key = await new Promise((resolve) => {
      s3Api.uploadAttachment.mutate(file, { onSuccess: (key) => resolve(key) });
    });

    const { data } = await httpClient.post(`${baseUrl}/${attachment.ideaId}/attachments`, {
      ...attachment,
      attachmentUrl: key,
    });

    return data;
  };

  const getIdeaStatusSummary = async (): Promise<IdeaStatusSummaryDto> => {
    const { data } = await httpClient.get(`${baseUrl}/status-summary`);
    return data;
  };

  const getIdeaAttachments = async (id: number): Promise<GetIdeaAttachmentsResponseDto> => {
    const { data } = await httpClient.get(`${baseUrl}/${id}/attachments`);
    return data;
  };

  const removeIdeaAttachment = async (params: { ideaId: number; id: number }) => {
    const { data } = await httpClient.delete(
      `${baseUrl}/${params.ideaId}/attachments/${params.id}`,
    );
    return { data };
  };

  const getIdeaRoadmapItems = async (id: number): Promise<string[]> => {
    const { data } = await httpClient.get(`${baseUrl}/${id}/roadmap-items`);
    return [...data];
  };

  const linkIdeaRoadmapItem = async (
    linkedItem: LinkedRoadmapItemDto,
  ): Promise<LinkedRoadmapItemDto> => {
    const { ideaId, ...rest } = linkedItem;
    const { data } = await httpClient.post(`${baseUrl}/${ideaId}/roadmap-items`, rest);
    return data;
  };

  const getIdeaCustomers = async (id: number): Promise<GetIdeaAttachmentsResponseDto> => {
    const { data } = await httpClient.get(`${baseUrl}/${id}/customers`);
    return data;
  };

  const getIdeaRecipients = async (
    id: number,
    offset: number,
    limit = 100,
  ): Promise<GetIdeaRecipientsResponseDto> => {
    const { data } = await httpClient.get(`${baseUrl}/${id}/recipients`, {
      params: { limit, offset },
    });
    return data;
  };

  const linkIdeaCustomer = async (params: { id: number; customerId: number }): Promise<string> => {
    const { data } = await httpClient.post(`${baseUrl}/${params.id}/customers`, {
      customerId: params.customerId,
    });
    return data;
  };

  const unlinkIdeaCustomer = async (params: {
    id: number;
    customerId: number;
  }): Promise<string> => {
    const { data } = await httpClient.delete(
      `${baseUrl}/${params.id}/customers/${params.customerId}`,
    );
    return data;
  };

  const getIdeas = async (
    params?: GetAllParamsDto & {
      filter?: FilterSchemaDto;
      viewId?: number;
    },
  ) => {
    const { data } = await httpClient.post(`${baseUrl}/getIdeas`, params);
    const userIds = data.data.map((el: IdeaSummaryDto) => el.ownerId);
    const result = await getUserDataForResults(
      httpClient,
      userIds,
      data.data,
      (item: any) => item.ownerId,
    );
    return {
      ...data,
      data: result,
    } as {
      data: IdeaSummaryDto[];
      pagination: { offset: number; limit: number; total: number };
    };
  };

  const useIdeas = (
    viewId?: number,
    params?: GetAllParamsDto & {
      filter?: FilterSchemaDto;
      matchingIdeaIds?: number[];
    },
    options?: {
      keepPreviousData?: boolean;
      enabled?: boolean;
    },
  ) => {
    options = options || {};
    const limit = params?.limit ?? defaultLimit;
    return useInfiniteQuery({
      initialPageParam: 0,
      gcTime: 0,
      queryKey: [baseUrl, 'list', params?.lifecycleFilter, params],
      queryFn: ({ pageParam }) => {
        const newParams = {
          ...params,
          limit,
          offset: pageParam * limit,
          viewId: viewId,
        };
        return getIdeas(newParams);
      },
      getNextPageParam: (lastPage) => {
        return lastPage.data.length === limit ? lastPage.pagination.offset / limit + 1 : undefined;
      },
      enabled: options.enabled,
    });
  };

  const getMatrixIdeas = async ({ params }: { params?: unknown }) => {
    const { data } = await httpClient.post<{
      data: (MatrixIdeaSummaryDto & { id: number })[];
    }>(`${baseUrl}/getMatrixIdeas`, params);

    return data;
  };

  const useMatrixIdeas = (
    viewId?: number,
    params: {
      filter?: FilterSchemaDto;
      matchingIdeaIds?: number[];
    } = {},
    enabled = false,
  ) => {
    const queryKey: QueryKey = ['getMatrixIdeas', baseUrl, viewId, params].filter(Boolean);

    return useQuery({
      queryKey,
      queryFn: () => getMatrixIdeas({ params: { ...params, viewId } }),
      enabled: enabled,
    });
  };

  // Update Idea data in the react-query cache.
  const ideasSetCachedData = (idea: IdeaSummaryDto) => {
    if (idea.rank === null) {
      // An idea only has a rank in the context of a view.
      //
      // The update endpoint is not associated with a view,
      // so it will always return an idea with a null rank.
      //
      // Treat a null rank as undefined so we don't overwrite
      // a valid rank with null when editing ideas in the grid.
      delete idea.rank;
    }

    queryClient.setQueryData([baseUrl, idea.id], (oldData: { data: IdeaSummaryDto }) => {
      if (oldData?.data?.id === idea.id) {
        oldData.data = { ...oldData.data, ...idea };
      }
      return structuredClone(oldData);
    });

    const infiniteQueryKeys = queryClient
      .getQueryCache()
      .getAll()
      .filter((q) => {
        return q.queryKey[0] === baseUrl && q.queryKey[1] === 'list';
      });

    // update infinite query caches
    infiniteQueryKeys.forEach((key) => {
      queryClient.setQueryData(
        key.queryKey,
        (oldData: InfiniteData<UseInfiniteQueryResult<IdeaSummaryDto[]>> | undefined) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              data: (page.data || []).map((item) =>
                item.id === idea.id ? { ...item, ...idea } : item,
              ),
            })),
          };
        },
      );
    });

    // Update matrix query caches (IdeasMatrixView)
    // The matrix view displays ideas in a different format/structure than the regular list view.
    // When an idea is updated via the regular update endpoint, we need to ensure the matrix view
    // cache is also updated to maintain data consistency across different views of the same data.
    // This prevents stale data issues where the matrix view would show outdated information
    // until the next refetch, providing a seamless user experience.
    const matrixQueries = queryClient.getQueriesData({ queryKey: ['getMatrixIdeas', baseUrl] });
    matrixQueries.forEach(([key]) => {
      queryClient.setQueryData(
        key,
        (matrixData?: { data?: (MatrixIdeaSummaryDto & { id: number })[] }) => {
          if (matrixData?.data) {
            const updatedData = matrixData.data.map((matrixIdea) => {
              if (matrixIdea.id === idea.id) {
                return { ...matrixIdea, ...idea };
              }
              return matrixIdea;
            });
            return { ...matrixData, data: updatedData };
          }
          return matrixData;
        },
      );
    });
  };

  const getIdeaWithViewCustomFieldsSynced = async (params: {
    ideaId: number;
    ideaView: GetIdeaViewByIdResponseDto;
  }) => {
    const ideaData = await getIdea(params.ideaId);
    const ideaView = params.ideaView;

    if (ideaView.data?.customFields) {
      const updatedIdeaFields = getIdeaFieldsWithMissingCustomFieldsAdded(
        ideaView.data.customFields,
        ideaData.data,
      );
      if (updatedIdeaFields.length !== 0) {
        const { data: updatedIdeaData } = await httpClient.put(`${baseUrl}/${params.ideaId}`, {
          fields: updatedIdeaFields,
          id: undefined,
        });
        if (updatedIdeaData.data.fields) {
          ideaData.data.fields = updatedIdeaData.data.fields;
        }
      }
    }

    return ideaData;
  };

  const getIdeasLinkedToItem = async (
    params?: GetAllParamsDto & {
      filter?: LinkedRoadmapFilterDto;
    },
  ): Promise<GetLinkedRoadmapItemsResponseDto> => {
    const { data } = await httpClient.post(`${baseUrl}/linked`, params);
    return data;
  };

  const useGetIdeasLinkedToItem = (
    params?: GetAllParamsDto & {
      filter?: LinkedRoadmapFilterDto;
    },
  ) => {
    return useQuery({
      queryKey: [baseUrl, 'linked', params],
      queryFn: () => getIdeasLinkedToItem(params),
    });
  };

  const getIdea = async (ideaId: number) => {
    const { data } = await httpClient.get(`${baseUrl}/${ideaId}`);

    const result = await getUserDataForResults(
      httpClient,
      [data.data.ownerId],
      [data.data],
      (item: any) => item.ownerId,
    );

    return { data: result[0] as IdeaSummaryDto & { displayName?: string } };
  };

  const useGetIdea = (
    params: { ideaId: number; ideaView?: GetIdeaViewByIdResponseDto },
    disabled?: boolean,
  ) => {
    return useQuery({
      queryKey: [baseUrl, params.ideaId],
      queryFn: () =>
        params.ideaView
          ? getIdeaWithViewCustomFieldsSynced({ ...params, ideaView: params.ideaView })
          : getIdea(params.ideaId),
      enabled: !disabled,
    });
  };

  const getIdeaComments = async (ideaId: number) => {
    const { data } = await httpClient.get(`${baseUrl}/${ideaId}/comments`);

    const userIds = data.data.map((el: IdeaCommentsDto) => el.commenterId);

    const result = await getUserDataForResults(
      httpClient,
      userIds,
      data.data,
      (item: any) => item.commenterId,
    );
    return result;
  };

  const useIdeaComments = ({ id }: { id: number }) =>
    useQuery({
      queryKey: [baseUrl, id, 'comments'],
      queryFn: () => getIdeaComments(id),
    });

  const useIdeaAttachments = (id: number) => {
    return useQuery({
      queryKey: [baseUrl, id, 'attachments'],
      queryFn: () => getIdeaAttachments(id),
    });
  };

  const useIdeaCustomers = (id: number) => {
    return useQuery({
      queryKey: [baseUrl, id, 'customers'],
      queryFn: () => getIdeaCustomers(id),
    });
  };

  const useGetIdeaRecipients = (id: number, limit = 100) => {
    return useInfiniteQuery({
      queryKey: [baseUrl, id, 'recipients'],
      queryFn: ({ pageParam = 0 }) => getIdeaRecipients(id, pageParam, limit),
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        const fetched = allPages.reduce(
          (sum, p) => sum + p.contacts.length + p.votersAndCommenters.length,
          0,
        );
        return fetched < lastPage.totalCount ? fetched : undefined;
      },
      enabled: !!id,
    });
  };

  const useGetIdeaFeedback = (id: number) => {
    return useQuery({
      queryKey: [baseUrl, id, 'feedback'],
      queryFn: () => getIdeaFeedback(id),
      enabled: !!id,
    });
  };
  const useGetIdeaCount = (
    lifecycleFilter?: LifecycleFilterType | undefined,
    search?: string | undefined,
    filter?: FilterSchemaDto,
    enabled?: boolean,
  ) => {
    return useQuery({
      queryKey: [baseUrl, 'count', lifecycleFilter, search, filter],
      queryFn: () => getIdeaCount(lifecycleFilter, search, filter),
      enabled,
    });
  };

  const REVIEW_REASON_PATH_MAP: Record<ReviewReasonType, string> = {
    stale: 'stale',
    no_votes: 'no-votes',
    ready_to_archive: 'ready-to-archive',
  };

  const getIdeasRequiringReview = async (
    reviewReason: ReviewReasonType,
    productAreaId: number | undefined,
    limit?: number,
  ): Promise<GetIdeasRequiringReviewResponseDto> => {
    const path = REVIEW_REASON_PATH_MAP[reviewReason];
    const { data } = await httpClient.post(`${baseUrl}/requiring-review/${path}`, {
      ...(productAreaId !== undefined && { productAreaId }),
      limit,
    });
    return data;
  };

  const useIdeasRequiringReview = (
    reviewReason: ReviewReasonType,
    productAreaId: number | undefined,
    options?: { limit?: number; enabled?: boolean },
  ) => {
    return useQuery({
      queryKey: [baseUrl, 'requiring-review', reviewReason, productAreaId ?? 'all', options?.limit],
      queryFn: () => getIdeasRequiringReview(reviewReason, productAreaId, options?.limit),
      enabled: options?.enabled ?? true,
    });
  };

  const useGetIdeaStatusSummary = () => {
    return useQuery({
      queryKey: [baseUrl, 'status-summary'],
      queryFn: () => getIdeaStatusSummary(),
    });
  };

  const useGetIdeaRoadmapItems = (id: number) => {
    return useQuery({
      queryKey: [baseUrl, id, 'roadmap-items'],
      queryFn: () => getIdeaRoadmapItems(id),
      enabled: !!id,
    });
  };

  const getIdeaActivityLog = async (id: number, logType = '') => {
    const { data } = await httpClient.get(`${baseUrl}/${id}/activity-logs`, {
      params: { logType },
    });

    const userIds = data.map((el: ActivityLogDto) => el.userId);

    const result = await getUserDataForResults(
      httpClient,
      userIds,
      data,
      (item: any) => item.userId,
    );

    return result;
  };

  const useGetIdeaActivityLog = (id: number, logType = '') =>
    useQuery({
      queryKey: ['ideas', id, 'activity-logs', logType],
      queryFn: () => getIdeaActivityLog(id, logType),
    });

  const getLinkedRoadmapIdeas = async (
    params?: GetAllParamsDto & {
      filter?: LinkedRoadmapFilterDto;
    },
  ) => {
    const { data } = await httpClient.post(`${baseUrl}/linked-roadmap`, params);
    return data;
  };

  const addFramework = async ({
    id,
    frameworkId,
  }: {
    id: number;
    frameworkId: number;
  }): Promise<UpdateIdeaSummaryResponseDto> => {
    const { data } = await httpClient.put(`${baseUrl}/${id}/framework/${frameworkId}`);
    return data;
  };

  const useGetLinkedRoadmapIdeas = (
    params?: GetAllParamsDto & {
      filter?: LinkedRoadmapFilterDto;
    },
  ) => {
    return useQuery({
      queryKey: [baseUrl, 'linked'],
      queryFn: () => getLinkedRoadmapIdeas(params),
      enabled: !!params,
    });
  };

  const queryClient = useQueryClient();

  const create = useErrorHandlingMutation({
    mutationFn: createIdea,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [baseUrl] }),
  });
  const createFromFeedback = useErrorHandlingMutation({
    mutationFn: createIdeaFromFeedback,
    onSuccess: () => {
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: [baseUrl] }),
        queryClient.invalidateQueries({ queryKey: ['feedback'] }),
      ]);
    },
  });

  const update = useErrorHandlingMutation({
    mutationFn: updateIdea,
    onSuccess: (data) => {
      ideasSetCachedData(data.data);
      queryClient.invalidateQueries({ queryKey: ['ideas', data.data.id, 'activity-logs'] });
    },
  });

  const useBulkArchive = () =>
    useErrorHandlingMutation({
      mutationFn: bulkArchiveIdeas,
      onSuccess: () =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: [baseUrl] }),
          queryClient.resetQueries({
            queryKey: [baseUrl, 'list', LifecycleFilter.Archived],
          }),
        ]),
    });

  const useBulkRestore = () =>
    useErrorHandlingMutation({
      mutationFn: bulkRestoreIdeas,
      onSuccess: () =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: [baseUrl] }),
          queryClient.resetQueries({
            queryKey: [baseUrl, 'list', LifecycleFilter.Active],
          }),
        ]),
    });

  const useBulkDelete = () =>
    useErrorHandlingMutation({
      mutationFn: bulkDeleteIdeas,
      onSuccess: () =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: [baseUrl] }),
          queryClient.resetQueries({ queryKey: [baseUrl, 'list'] }),
        ]),
    });

  const useArchive = () =>
    useErrorHandlingMutation({
      mutationFn: archiveIdea,
      onSuccess: () =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: [baseUrl] }),
          queryClient.resetQueries({
            queryKey: [baseUrl, 'list', LifecycleFilter.Archived],
          }),
        ]),
    });

  const useRestore = () =>
    useErrorHandlingMutation({
      mutationFn: restoreIdea,
      onSuccess: () =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: [baseUrl] }),
          queryClient.resetQueries({
            queryKey: [baseUrl, 'list', LifecycleFilter.Active],
          }),
        ]),
    });

  const useDelete = () =>
    useErrorHandlingMutation({
      mutationFn: deleteIdea,
      onSuccess: () => queryClient.invalidateQueries({ queryKey: [baseUrl] }),
    });

  const createComment = useErrorHandlingMutation({
    mutationFn: createIdeaComment,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [baseUrl] }),
  });

  const updateComment = useErrorHandlingMutation({
    mutationFn: updateIdeaComment,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [baseUrl] }),
  });

  const useArchiveComment = () =>
    useErrorHandlingMutation({
      mutationFn: archiveComment,
      onSuccess: (data) =>
        Promise.all([
          queryClient.invalidateQueries({
            queryKey: [baseUrl, data.data.ideaId, 'comments'],
          }),
        ]),
    });

  const useRestoreComment = () =>
    useErrorHandlingMutation({
      mutationFn: restoreComment,
      onSuccess: (data) =>
        Promise.all([
          queryClient.invalidateQueries({
            queryKey: [baseUrl, data.data.ideaId, 'comments'],
          }),
        ]),
    });

  const useDeleteComment = () =>
    useErrorHandlingMutation({
      mutationFn: deleteComment,
      onSuccess: (data) => {
        queryClient.invalidateQueries({
          queryKey: [baseUrl, data.data.ideaId, 'comments'],
        });
      },
    });

  const createAttachment = useErrorHandlingMutation({
    mutationFn: createIdeaAttachment,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [baseUrl] }),
  });

  const removeAttachment = useErrorHandlingMutation({
    mutationFn: removeIdeaAttachment,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [baseUrl] }),
  });

  const linkRoadmapItem = useErrorHandlingMutation({
    mutationFn: linkIdeaRoadmapItem,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [baseUrl] });
      queryClient.invalidateQueries({ queryKey: ['ideas', variables.ideaId, 'roadmap-items'] });
    },
  });

  const unlinkIdeaRoadmapItem = async (params: { ideaId: number; roadmapItemId: string }) => {
    const { data } = await httpClient.delete(
      `${baseUrl}/${params.ideaId}/roadmap-items/${params.roadmapItemId}`,
    );
    return data;
  };

  const unlinkRoadmapItem = useErrorHandlingMutation({
    mutationFn: unlinkIdeaRoadmapItem,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [baseUrl] }),
  });

  const linkCustomer = useErrorHandlingMutation({
    mutationFn: linkIdeaCustomer,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [baseUrl] }),
  });

  const unlinkCustomer = useErrorHandlingMutation({
    mutationFn: unlinkIdeaCustomer,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [baseUrl] }),
  });

  const useAddFramework = () =>
    useErrorHandlingMutation({
      mutationFn: addFramework,
      onSuccess: (data) => {
        queryClient.invalidateQueries({ queryKey: [baseUrl, data.data.id] });
      },
    });

  const getIdeaPortals = async (ideaId: number): Promise<IdeaPortalAssociationDto[]> => {
    const { data } = await httpClient.get(`${baseUrl}/${ideaId}/portals`);
    return data;
  };

  const publishToPortals = async (
    ideaId: number,
    portalIds: number[],
  ): Promise<GetIdeaResponseDto> => {
    const { data } = await httpClient.put(`${baseUrl}/${ideaId}/portals`, {
      portalIds,
    });
    return data;
  };

  return {
    create,
    createFromFeedback,
    update,
    useGetIdea,
    useIdeas,
    getIdeas,
    ideasSetCachedData,
    useArchive,
    useRestore,
    useDelete,
    useBulkArchive,
    useBulkRestore,
    useBulkDelete,
    useIdeaComments,
    createComment,
    updateComment,
    useArchiveComment,
    useRestoreComment,
    useDeleteComment,
    useIdeaAttachments,
    createAttachment,
    removeAttachment,
    useIdeaCustomers,
    useGetIdeaFeedback,
    useGetIdeaRoadmapItems,
    linkRoadmapItem,
    unlinkRoadmapItem,
    useGetIdeaActivityLog,
    useGetLinkedRoadmapIdeas,
    linkCustomer,
    unlinkCustomer,
    useGetIdeaCount,
    useIdeasRequiringReview,
    useGetIdeaStatusSummary,
    useMatrixIdeas,
    useGetIdeasLinkedToItem,
    useAddFramework,
    getIdeaPortals,
    publishToPortals,
    useGetIdeaRecipients,
  };
};
